const RELOAD_MARKER = 'lovatar:admin:stale-chunk-reload';
const RELOAD_COOLDOWN_MS = 60_000;
const CACHE_BUST_PARAM = '__lovatar_release';

const STALE_CHUNK_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk .* failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
];

export function isStaleChunkError(value: unknown): boolean {
  const message =
    value instanceof Error
      ? value.message
      : typeof value === 'string'
        ? value
        : '';

  return STALE_CHUNK_PATTERNS.some((pattern) => pattern.test(message));
}

export function buildStaleChunkRecoveryUrl(currentHref: string, timestamp: number): string {
  const url = new URL(currentHref);
  url.searchParams.set(CACHE_BUST_PARAM, String(timestamp));
  return url.toString();
}

interface StaleChunkRecoveryOptions {
  now?: number;
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  location?: Pick<Location, 'href' | 'replace'>;
}

/**
 * Requests a cache-busted navigation to the current route. This is shared by
 * the global browser listeners and Angular Router's navigation error handler,
 * because the router can consume a failed lazy import before it becomes an
 * unhandled window error.
 */
export function requestStaleChunkRecovery(
  value: unknown,
  options: StaleChunkRecoveryOptions = {},
): boolean {
  if (!isStaleChunkError(value)) {
    return false;
  }

  const now = options.now ?? Date.now();
  const storage = options.storage ?? window.sessionStorage;
  const location = options.location ?? window.location;
  const previousValue = storage.getItem(RELOAD_MARKER);
  const previousReload = previousValue === null ? Number.NaN : Number(previousValue);
  if (Number.isFinite(previousReload) && now - previousReload < RELOAD_COOLDOWN_MS) {
    return false;
  }

  storage.setItem(RELOAD_MARKER, String(now));
  location.replace(buildStaleChunkRecoveryUrl(location.href, now));
  return true;
}

/**
 * Recovers a tab that stayed open while a new release replaced lazy-loaded
 * JavaScript chunks. Only one reload is allowed per minute to avoid loops when
 * an asset is genuinely unavailable on the server.
 */
export function registerStaleChunkRecovery(): void {
  const recover = (value: unknown, event: Event): void => {
    if (requestStaleChunkRecovery(value)) {
      event.preventDefault();
    }
  };

  window.addEventListener('unhandledrejection', (event) => {
    recover(event.reason, event);
  });

  window.addEventListener('error', (event) => {
    recover(event.error ?? event.message, event);
  });
}
